import { useCallback, useEffect, useState } from 'react';

import debounce from 'lodash/debounce';
import Select, { MultiValue } from 'react-select';

import { useFilter } from '@features/table-filter';
import { IDataResponse, TFetchQueryError } from '@shared/types/fetch-data';
import { IZodValidationError } from '@shared/zod';

import { reactFormSelectMultiStyles } from '../react-select-styles';

export interface ISelectMultiOption {
  value: string;
  label: string;
}

export type ISelectMultiOptions = ISelectMultiOption[] | MultiValue<ISelectMultiOption>;

interface IProps<T, Res extends IDataResponse<T>> {
  name: string;
  data: Res;
  getOptionLabel: (option: T) => string;
  getOptionValue: (option: T) => string;
  selectedValue?: string;
  onChange?: (value: string) => void;
  isLoading: boolean;
  filter: ReturnType<typeof useFilter>;
  error: IZodValidationError | TFetchQueryError;
  filterKey?: string;
  isDisabled?: boolean;
}

const DEBOUNCE_DELAY = 300;
const DEBOUNCE_OPTIONS = { leading: true };
export const SelectMultiAsync = <T, Res extends IDataResponse<T>>({
  name,
  data,
  getOptionLabel,
  getOptionValue,
  selectedValue,
  isDisabled,
  isLoading,
  filter,
  filterKey = 'name',
}: IProps<T, Res>) => {
  const [selectedOptions, setSelectedOptions] = useState<MultiValue<ISelectMultiOption>>([]);
  const [inputValue, setInputValue] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const [options, setOptions] = useState<ISelectMultiOption[]>([]);

  const { setPage, updateFilter } = filter;
  const setQuery = useCallback(
    (query: string) => updateFilter({ [`filter[${filterKey}]`]: query }),
    [updateFilter, filterKey],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedAsyncOptions = useCallback(
    debounce(
      (input: string) => {
        if (setQuery) setQuery(input);
      },
      DEBOUNCE_DELAY,
      DEBOUNCE_OPTIONS,
    ),
    [setQuery],
  );

  useEffect(() => {
    if (data) {
      const newOptions = data.data.map(item => ({
        value: getOptionValue(item),
        label: getOptionLabel(item),
      }));

      setOptions(prevOptions => {
        if (data.meta.current_page === 1) {
          return newOptions;
        }
        const combinedOptions = [...prevOptions, ...newOptions];
        const uniqueOptions = combinedOptions.filter(
          (option, index, self) => index === self.findIndex(o => o.value === option.value),
        );
        return uniqueOptions;
      });

      !data.links.next ? setHasMore(false) : setHasMore(true);
    }
  }, [data, getOptionLabel, getOptionValue]);

  useEffect(() => {
    if (selectedValue && options.length > 0 && selectedOptions.length !== 0) {
      const selected = options.filter(option => selectedValue.includes(option.value));
      setSelectedOptions(selected);
    }
  }, [selectedValue, options, selectedOptions]);

  useEffect(() => {
    return () => debouncedAsyncOptions.cancel();
  }, [debouncedAsyncOptions]);
  const handleScrollToBottom = useCallback(() => {
    if (hasMore) setPage(prevPage => (Number(prevPage) + 1).toString());
  }, [hasMore, setPage]);

  const handleInputChange = useCallback(
    (input: string) => {
      setInputValue(input);
      if (input.trim() === '') {
        setQuery('');
        return;
      }
      debouncedAsyncOptions(input);
    },
    [debouncedAsyncOptions, setQuery],
  );

  const handleChange = (newValue: MultiValue<ISelectMultiOption>) => {
    setSelectedOptions(newValue);
  };

  return (
    <div className='select-multi-container'>
      <Select
        isMulti
        classNamePrefix='react-select'
        className='react-select-container'
        inputValue={inputValue}
        isLoading={isLoading}
        isClearable
        hideSelectedOptions={false}
        maxMenuHeight={200}
        isDisabled={isDisabled}
        menuPosition='fixed'
        styles={reactFormSelectMultiStyles}
        menuShouldBlockScroll={true}
        menuShouldScrollIntoView={false}
        onChange={handleChange}
        onInputChange={handleInputChange}
        options={options}
        value={selectedOptions}
        closeMenuOnSelect
        onMenuScrollToBottom={handleScrollToBottom}
      />

      {/* Hidden inputs for form submission */}
      {selectedOptions.map(option => (
        <input key={option.value} type='hidden' name={name} value={option.value} />
      ))}
    </div>
  );
};
